import React from 'react';
import { CalendarDays, TrendingUp, Percent, CreditCard, PiggyBank, DollarSign, Landmark, BarChart2 } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency, formatPercent } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета дивидендов
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Вспомогательная функция для форматирования числа в валюте
  const formatCurrencyWithSymbol = (value) => {
    return `${results.currencySymbol} ${formatNumber(value, 2)}`;
  };

  // Вспомогательная функция для форматирования процентов
  const formatPercentValue = (value) => {
    return `${formatNumber(value * 100, 2)}%`;
  };

  return (
    <ToolResult title="Результаты расчета дивидендов">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общий доход от дивидендов */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Чистый доход от дивидендов
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.totalNetDividends)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            За {results.yearsProjected} {formatYearsLabel(results.yearsProjected)} с учетом налогов
          </p>
        </div>

        {/* Итоговая стоимость портфеля */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <BarChart2 className="w-4 h-4"/>
            Конечная стоимость портфеля
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.finalPortfolioValue)}
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              ({formatPercentValue(results.totalReturn)} доходность)
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Включает {formatNumber(results.finalSharesCount, 2)} акций и все полученные дивиденды
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальные инвестиции" 
          value={formatCurrencyWithSymbol(results.initialInvestment)} 
          color="indigo" 
          icon={<Landmark className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Дивидендная доходность" 
          value={`${formatPercentValue(results.dividendYield)} годовых`} 
          color="green" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Периодичность выплат" 
          value={formatFrequency(results.paymentsFrequency)} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Общая сумма дивидендов (до налогов)" 
          value={formatCurrencyWithSymbol(results.totalDividends)} 
        />
        
        <ResultItem 
          label="Сумма уплаченных налогов" 
          value={formatCurrencyWithSymbol(results.totalTaxesPaid)} 
          color="red" 
        />
        
        <ResultItem 
          label="Чистые дивиденды (после налогов)" 
          value={formatCurrencyWithSymbol(results.totalNetDividends)} 
          color="green" 
        />
        
        <ResultItem 
          label="Количество акций (конечное)" 
          value={formatNumber(results.finalSharesCount, 2)} 
          color="purple" 
          icon={<CreditCard className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Цена акции (конечная)" 
          value={formatCurrencyWithSymbol(results.finalStockPrice)} 
          color="blue" 
          icon={<TrendingUp className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Средняя годовая доходность" 
          value={formatPercentValue(results.annualReturn)} 
          color="green" 
          icon={<TrendingUp className="w-4 h-4"/>} 
        />

        {/* Информация о дивидендной стратегии */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <PiggyBank className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Дивидендная стратегия</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Данный расчет показывает, что при инвестировании {formatCurrencyWithSymbol(results.initialInvestment)} в акции с 
                дивидендной доходностью {formatPercentValue(results.dividendYield)} вы можете 
                получить {formatCurrencyWithSymbol(results.totalNetDividends)} чистого дивидендного дохода за {results.yearsProjected} {formatYearsLabel(results.yearsProjected)}.
                Общая доходность инвестиций составит {formatPercentValue(results.totalReturn)}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

// Форматирование периодичности выплат
const formatFrequency = (frequency) => {
  switch (frequency) {
    case 'annually': return 'Ежегодно (1 раз в год)';
    case 'semi_annually': return 'Раз в полгода (2 раза в год)';
    case 'quarterly': return 'Ежеквартально (4 раза в год)';
    case 'monthly': return 'Ежемесячно (12 раз в год)';
    default: return 'Не указано';
  }
};

export default ResultsOverview;